/**
 * DocCard Component
 *
 * Displays a document card with title, preview text, and date.
 * Used in Grid View and Flow View layouts.
 *
 * @module features/dashboard/components/DocCard
 * @see SPEC-016 Requirement 4.2
 * @see SPEC-017 Task 6.2 - i18n integration
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/cn';
import { useTypedTranslation } from '../../../i18n';
import { formatSmartDate, formatDateTime } from '../../../i18n/formatters';
import type { SupportedDateLocale } from '../../../i18n/formatters';
import type { DocListItemInput } from '@linkode/shared';

// ============================================================================
// Variants
// ============================================================================

/**
 * DocCard variants using CVA
 */
const docCardVariants = cva(
  [
    'rounded-linkode',
    'border-2 border-linkode-text',
    'bg-white',
    'p-4',
    'flex flex-col gap-2',
    'transition-all duration-200',
    'cursor-pointer',
    'hover:shadow-linkode-card hover:-translate-y-1',
    'focus:outline-none focus:ring-2 focus:ring-linkode-teal',
  ],
  {
    variants: {
      selected: {
        true: 'ring-2 ring-linkode-primary bg-linkode-bg-orange',
        false: '',
      },
      compact: {
        true: 'p-3 gap-1',
        false: 'p-4 gap-2',
      },
    },
    defaultVariants: {
      selected: false,
      compact: false,
    },
  }
);

/** Exported type for DocCard variants */
export type DocCardVariants = VariantProps<typeof docCardVariants>;

// ============================================================================
// Types
// ============================================================================

export interface DocCardProps extends DocCardVariants {
  /** Document data */
  doc: DocListItemInput;
  /** Click handler */
  onClick?: (doc: DocListItemInput) => void;
  /** Context menu handler (right-click) */
  onContextMenu?: (doc: DocListItemInput, event: React.MouseEvent) => void;
  /** Additional class names */
  className?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Truncate text to specified length with ellipsis
 */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

/**
 * Get locale from i18n language code
 */
function getLocaleFromLanguage(language: string): SupportedDateLocale {
  if (language === 'zh-TW' || language.startsWith('zh')) {
    return 'zh-TW';
  }
  return 'en';
}

// ============================================================================
// Component
// ============================================================================

/**
 * DocCard Component
 *
 * Displays a document card with:
 * - Title (truncated if too long)
 * - Preview text (first 200 characters)
 * - Updated date (relative format)
 * - Shared indicator
 *
 * @example
 * ```tsx
 * <DocCard
 *   doc={document}
 *   onClick={(doc) => navigate(`/editor/${doc.docId}`)}
 *   onContextMenu={(doc, e) => showContextMenu(e, doc)}
 * />
 * ```
 *
 * @see Requirement 4.2
 */
export const DocCard: React.FC<DocCardProps> = ({
  doc,
  onClick,
  onContextMenu,
  selected,
  compact,
  className,
}) => {
  const { t, i18n } = useTypedTranslation();
  const locale = getLocaleFromLanguage(i18n.language);

  const handleClick = () => {
    onClick?.(doc);
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    onContextMenu?.(doc, event);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.(doc);
    }
  };

  const updatedDate = new Date(doc.updatedAt);

  return (
    <article
      className={cn(docCardVariants({ selected, compact }), className)}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`Document: ${doc.title}`}
      aria-selected={selected ?? false}
    >
      {/* Title */}
      <h3 className="font-mono font-bold text-linkode-text truncate" title={doc.title}>
        {truncateText(doc.title || 'Untitled', 50)}
      </h3>

      {/* Preview Text */}
      {!compact && doc.previewText && (
        <p className="text-sm text-linkode-text/70 line-clamp-2">
          {truncateText(doc.previewText, 100)}
        </p>
      )}

      {/* Footer: Date and Shared indicator */}
      <div className="flex items-center justify-between mt-auto pt-2">
        <time
          className="text-xs text-linkode-text/50 font-mono"
          dateTime={updatedDate.toISOString()}
          title={formatDateTime(updatedDate, locale)}
        >
          {formatSmartDate(updatedDate, locale)}
        </time>

        {doc.isShared && (
          <span
            className="text-xs text-linkode-teal font-mono"
            title={t('dashboard.dates.sharedDocument')}
            aria-label={t('dashboard.dates.shared')}
          >
            {t('dashboard.dates.shared')}
          </span>
        )}
      </div>
    </article>
  );
};

// Export variants for testing
export { docCardVariants };
